import { LOCALE_SEARCH_PATHS } from 'types/locales';
import { AutocompletePredictionPredictionCategory } from 'utils/google-maps/types';
import { 
  AVAILABLE_STATUS, 
  NOT_AVAILABLE_SOLD_STATUS, 
  NOT_AVAILABLE_OTHER_STATUS, 
  INACTIVE_STATUS, 
  EXPIRED_STATUS, 
} from 'contexts/preferences/listing-params/types';

type TFunction = (key: string, options?: { defaultValue?: string }) => string;

// Reuse existing status constants instead of creating our own
const STATUS_LABELS = {
  // Use constants where they exist
  [AVAILABLE_STATUS]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_ACTIVE, default: 'Active' }, // 'available'
  [NOT_AVAILABLE_SOLD_STATUS]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_SOLD, default: 'Sold' }, // 'not-available-sold'
  [NOT_AVAILABLE_OTHER_STATUS]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_EXPIRED, default: 'Expired' }, // 'not-available-other'
  [INACTIVE_STATUS]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_INACTIVE, default: 'Inactive' }, // 'inactive'
  [EXPIRED_STATUS]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_EXPIRED, default: 'Expired' }, // 'expired'
  
  // Additional labels that don't have constants
  active: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_ACTIVE, default: 'Active' },
  sold: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_SOLD, default: 'Sold' },
  leased: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_LEASED, default: 'Leased' },
  pending: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_PENDING, default: 'Pending' },
  'for sale': { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_FOR_SALE, default: 'For Sale' },
  'for rent': { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_STATUS_FOR_RENT, default: 'For Rent' },
} as const;

// Map AutocompletePredictionPredictionCategory enum + additional types (school, building, state)
const LOCATION_LABELS = {
  [AutocompletePredictionPredictionCategory.CITY]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_CITY, default: 'City' },
  [AutocompletePredictionPredictionCategory.NEIGHBORHOOD]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_NEIGHBOURHOOD, default: 'Neighbourhood' },
  neighborhood: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_NEIGHBOURHOOD, default: 'Neighbourhood' }, // US spelling
  [AutocompletePredictionPredictionCategory.STREET]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_STREET, default: 'Street' },
  [AutocompletePredictionPredictionCategory.PROVINCE]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_PROVINCE, default: 'Province' },
  state: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_STATE, default: 'State' }, // Not in enum but used
  [AutocompletePredictionPredictionCategory.MILITARY]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_MILITARY, default: 'Military Base' },
  'military base': { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_MILITARY, default: 'Military Base' },
  school: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_SCHOOL, default: 'School' }, // Not in enum but used
  building: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_BUILDING, default: 'Building' }, // Not in enum but used
  [AutocompletePredictionPredictionCategory.OTHER]: { key: LOCALE_SEARCH_PATHS.PREDICTION_LABELS_LOCATION_TYPES_OTHER, default: 'Other' },
} as const;

export function translateLabel(t: TFunction, label: string): string {
  if (!label) return '';
  
  const normalized = label.toLowerCase().trim();
  const statusLabel = STATUS_LABELS[normalized as keyof typeof STATUS_LABELS];
  const locationLabel = LOCATION_LABELS[normalized as keyof typeof LOCATION_LABELS];
  
  if (statusLabel) return t(statusLabel.key, { defaultValue: statusLabel.default });
  if (locationLabel) return t(locationLabel.key, { defaultValue: locationLabel.default });
  
  return label;
}
